// useAppNavigate.ts

import { useNavigate, useParams, type NavigateOptions, type To } from "react-router-dom";
import { SUPPORTED_LANGUAGES } from "@/types/Languages.types";

const KNOWN_LANG_PREFIXES = [...(SUPPORTED_LANGUAGES as readonly string[])];

export function useAppNavigate() {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();

  function appNavigate(delta: number): void;
  function appNavigate(to: To, options?: NavigateOptions): void;
  function appNavigate(to: To | number, options?: NavigateOptions): void {
    if (typeof to === "number") {
      navigate(to);
      return;
    }

    const currentLang = lang || "zh-hant";

    const processPath = (path: string): string => {
      // Split pathname from search params (?) and hash (#)
      const queryOrHashIndex = path.search(/[?#]/);
      const pathname = queryOrHashIndex !== -1 ? path.slice(0, queryOrHashIndex) : path;
      const searchAndHash = queryOrHashIndex !== -1 ? path.slice(queryOrHashIndex) : "";

      const segments = pathname.split("/").filter(Boolean);

      let newPathname: string;
      if (segments.length > 0 && KNOWN_LANG_PREFIXES.includes(segments[0].toLowerCase())) {
        newPathname = "/" + segments.join("/");
      } else {
        const cleanPath = segments.join("/");
        newPathname = cleanPath === "" ? `/${currentLang}` : `/${currentLang}/${cleanPath}`;
      }

      return newPathname + searchAndHash;
    };

    if (typeof to === "string") {
      navigate(processPath(to), options);
      return;
    }

    if (typeof to === "object" && to.pathname) {
      navigate(
        {
          ...to,
          pathname: processPath(to.pathname),
        },
        options,
      );
      return;
    }

    navigate(to, options);
  }

  return appNavigate;
}
